import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Link } from '@lifeomic/chroma-react/components/Link';
import { useParams, Redirect } from 'react-router-dom';
import { useFeatures } from '@lifeomic/phc-web-toolkit/src/hooks';
import { useNotify } from '@lifeomic/phc-web-toolkit/src/hooks/useNotify';
import { apiHooks } from '@lifeomic/phc-web-toolkit/src/hooks/useRestAPIHooks';
import urls from '../../../../util/urls';
import {
  getFriendlyAction,
  getFriendlyChannel,
} from '../../AIRules/displayStrings';
import AuditMasterDetail from '../../AIRules/components/AuditMasterDetail';
import { RuleActionAuditRecord } from '../../AIRules/types';
import { formatDateTime, normalizeJson } from '../../AIRules/utils';
import MemberDetail from './index';
import { getMemberRuleActions } from './RuleActionAudit.utils';

const messages = defineMessages({
  emptyRuleActions: {
    id: 'MemberDetail.ruleActionAudit.emptyRuleActions',
    defaultMessage: 'No rule actions found for this member',
  },
  errorLoadingRuleActions: {
    id: 'MemberDetail.ruleActionAudit.errorLoadingRuleActions',
    defaultMessage: 'Error loading rule action audit',
  },
  selectRowHint: {
    id: 'MemberDetail.ruleActionAudit.selectRowHint',
    defaultMessage: 'Select a row to view details',
  },
});

type RuleActionAuditContentProps = {
  memberId: string;
};

export const RuleActionAuditContent: React.FC<RuleActionAuditContentProps> = ({
  memberId,
}) => {
  const { formatMessage } = useIntl();
  const [notify] = useNotify();
  const lastNotifiedError = useRef<string | null>(null);
  const [selectedRuleActionId, setSelectedRuleActionId] = useState<
    string | null
  >(null);

  const ruleActionsQuery = apiHooks.useAPIQuery(
    'GET /v1/agents-v2/zori-audit/rule-actions',
    {
      limit: 200,
    },
    {
      staleTime: 0,
      refetchOnWindowFocus: false,
      enabled: Boolean(memberId),
    },
  );

  useEffect(() => {
    const error = ruleActionsQuery.error;
    const message =
      error instanceof Error
        ? error.message
        : typeof error === 'string'
        ? error
        : null;
    if (!message) {
      lastNotifiedError.current = null;
      return;
    }
    if (lastNotifiedError.current !== message) {
      lastNotifiedError.current = message;
      notify(formatMessage(messages.errorLoadingRuleActions), 'error');
    }
  }, [formatMessage, notify, ruleActionsQuery.error]);

  const memberRuleActions = useMemo(
    () => getMemberRuleActions(ruleActionsQuery.data || [], memberId),
    [ruleActionsQuery.data, memberId],
  );

  const rowId = (row: RuleActionAuditRecord) =>
    `${row.timestamp}:${row.subject_id}:${row.rule_id || ''}:${row.action}`;

  const listColumns = useMemo(
    () => [
      {
        header: { label: 'Rule Action' },
        cell: {
          content: (row: RuleActionAuditRecord) => (
            <Box direction="column">
              <Box direction="row" justify="space-between" gap={1}>
                <Text size="table">{getFriendlyAction(row.action)}</Text>
                <Text
                  size="table"
                  color="default"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {row.subject_id}
                </Text>
              </Box>
              <Text size="table" color="default">
                {formatDateTime(row.timestamp)}
              </Text>
            </Box>
          ),
        },
      },
    ],
    [],
  );

  return (
    <AuditMasterDetail
      rows={memberRuleActions}
      loading={ruleActionsQuery.isLoading}
      emptyMessage={formatMessage(messages.emptyRuleActions)}
      noSelectionMessage={formatMessage(messages.selectRowHint)}
      rowId={rowId}
      listColumns={listColumns}
      detailTitle={(row: RuleActionAuditRecord) =>
        row.rule_id
          ? `${row.rule_id} - ${getFriendlyAction(row.action)}`
          : getFriendlyAction(row.action)
      }
      detailRows={(row: RuleActionAuditRecord) => [
        {
          label: 'Timestamp',
          value: formatDateTime(row.timestamp),
        },
        {
          label: 'Rule',
          value: row.rule_id ? (
            <Text size="table">
              <Link
                to={`${urls.app.workflows.zoriRules(
                  'all',
                )}?rule=${encodeURIComponent(row.rule_id)}`}
              >
                {row.rule_id}
              </Link>
            </Text>
          ) : (
            ''
          ),
        },
        { label: 'Action', value: getFriendlyAction(row.action) },
        {
          label: 'Channel',
          value: getFriendlyChannel(row.channel),
        },
        { label: 'Category', value: row.category || '' },
        { label: 'Status', value: row.status },
        {
          label: 'Subject',
          value: (
            <Text size="table">
              <Link to={urls.app.members(row.subject_id)}>
                {row.subject_id}
              </Link>
            </Text>
          ),
        },
        {
          label: 'Action Params',
          value: normalizeJson(row.action_params || {}),
        },
        { label: 'TTL', value: row.ttl ? String(row.ttl) : '' },
      ]}
      selectedRowId={selectedRuleActionId}
      setSelectedRowId={setSelectedRuleActionId}
    />
  );
};

const MemberRuleActionAudit: React.FC = () => {
  const hasFeature = useFeatures();
  const { memberId } = useParams<{ memberId: string }>();

  if (!hasFeature('fountainLifePowerUser')) {
    return <Redirect to={`${urls.app.members(memberId)}/annual-report`} />;
  }

  return <MemberDetail section="rule-action-audit" />;
};

MemberRuleActionAudit.displayName = 'MemberRuleActionAudit';
export default MemberRuleActionAudit;
